import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/en';
import 'dayjs/locale/ru';
import { authApi } from '../api';
import { NOTIFICATIONS } from '../constants/notificationsData';
import {
  DEFAULT_LANGUAGE,
  getActiveLanguage,
  getTranslation,
  normalizeLanguage,
} from '../translation';

const STORAGE_KEY = 'obank:notifications-read';
const NOTIFICATIONS_LIMIT = 25;
const NOTIFICATION_READ_BATCH_DELAY_MS = 800;
const NOTIFICATION_READ_BATCH_MAX_WAIT_MS = 2400;
const NOTIFICATION_READ_RETRY_DELAY_MS = 2000;
const NOTIFICATION_UPDATE_MODES = {
  MARK_AS_READ: 'mark_as_read',
};

const NOTIFICATION_GROUP_ORDER = ['today', 'earlier'];
const NOTIFICATION_ICON_VARIANTS = {
  new_login: 'login',
};

dayjs.extend(relativeTime);

const NOTIFICATION_TEXT_KEYS = {
  password_change: {
    title: 'passwordChangedTitle',
    description: 'passwordChangedText',
  },
  email_change: {
    title: 'emailChangedTitle',
    description: 'emailChangedText',
  },
  phone_change: {
    title: 'phoneChangedTitle',
    description: 'phoneChangedText',
  },
  order_new_status: {
    title: 'exchangeStatusTitle',
    description: 'exchangeStatusText',
  },
  order_status_updated: {
    title: 'orderStatusTitle',
    description: 'orderStatusText',
  },
  offline_beneficiary_failed: {
    title: 'offlineCheckFailedTitle',
    description: 'offlineCheckFailedText',
  },
  organization_approved: {
    title: 'accountApprovedTitle',
    description: 'accountApprovedText',
  },
  organization_rejected: {
    title: 'accountRejectedTitle',
    description: 'accountRejectedText',
  },
  organization_kyc_requested: {
    title: 'kycRequestedTitle',
    description: 'kycRequestedText',
  },
  new_login: {
    title: 'newLoginTitle',
    description: 'newLoginText',
  },
  otp_activated: {
    title: 'otpActivatedTitle',
    description: 'otpActivatedText',
  },
  otp_disabled: {
    title: 'otpDisabledTitle',
    description: 'otpDisabledText',
  },
  otp_recovery_key_updated: {
    title: 'recoveryKeyUpdatedTitle',
    description: 'recoveryKeyUpdatedText',
  },
};

function loadReadIds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return new Set();
    }
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function saveReadIds(readIds) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...readIds]));
}

function getLocalizedValue(value) {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'object') {
    const language = normalizeLanguage(getActiveLanguage()) || DEFAULT_LANGUAGE;
    const localized = value[language]
      || value[language.toUpperCase()]
      || value.en
      || value.EN
      || value.ru
      || value.RU
      || Object.values(value).find((item) => typeof item === 'string' && item.trim());

    return typeof localized === 'string' ? localized.trim() : '';
  }

  return '';
}

function getResponseNotifications(response) {
  if (Array.isArray(response)) {
    return response;
  }

  return Array.isArray(response?.data) ? response.data : [];
}

function getNotificationData(notification) {
  return notification?.data && typeof notification.data === 'object' ? notification.data : {};
}

function getNotificationType(notification) {
  return notification?.notification_type || notification?.type || '';
}

function getNotificationText(notification, key) {
  const data = getNotificationData(notification);
  const type = getNotificationType(notification);
  const language = normalizeLanguage(getActiveLanguage()) || DEFAULT_LANGUAGE;
  const notificationTranslations = getTranslation(language).notifications;
  const translationKey = NOTIFICATION_TEXT_KEYS[type]?.[key];
  const fallbackKey = key === 'title' ? 'fallbackTitle' : 'fallbackDescription';
  const value = data[key]
    || (translationKey ? notificationTranslations[translationKey] : null)
    || notificationTranslations[fallbackKey];

  return getLocalizedValue(value);
}

function getNotificationCreatedAt(notification) {
  const data = getNotificationData(notification);

  return notification.utc_datetime
    || notification.created_at
    || notification.createdAt
    || notification.generated_utc
    || data.utc_datetime
    || data.created_at
    || data.createdAt
    || data.generated_utc
    || null;
}

function getNotificationOrderId(notification) {
  const data = getNotificationData(notification);

  return data.order_id
    || data.orderId
    || data.task_id
    || data.taskId
    || data.route_id
    || data.routeId
    || null;
}

function normalizeNotificationLoginMeta(data = {}) {
  const location = data.ip_location && typeof data.ip_location === 'object'
    ? data.ip_location
    : data.ipLocation && typeof data.ipLocation === 'object'
      ? data.ipLocation
      : {};
  const country = location.country && typeof location.country === 'object' ? location.country : {};
  const rawCountryCode = String(country.alpha_2 || country.alpha2 || country.flag || '').trim().toUpperCase();
  const countryCode = rawCountryCode.length === 2 ? rawCountryCode : '';
  const loginMeta = {
    ip: String(data.ip || '').trim(),
    countryCode,
    countryTitle: String(country.title || country.title_en || country.alpha_2 || country.alpha_3 || '').trim(),
    city: String(location.city || data.city || '').trim(),
    userAgent: String(data.user_agent || data.userAgent || '').trim(),
  };

  return Object.values(loginMeta).some(Boolean) ? loginMeta : null;
}

function isToday(date) {
  const now = new Date();

  return date.getFullYear() === now.getFullYear()
    && date.getMonth() === now.getMonth()
    && date.getDate() === now.getDate();
}

function getNotificationDateGroup(createdAt) {
  if (!createdAt) {
    return 'earlier';
  }

  const date = new Date(createdAt);

  if (Number.isNaN(date.getTime())) {
    return 'earlier';
  }

  return isToday(date) ? 'today' : 'earlier';
}

export function formatNotificationTime(createdAt, language = getActiveLanguage()) {
  if (!createdAt) {
    return '';
  }

  const locale = normalizeLanguage(language) || DEFAULT_LANGUAGE;
  const date = dayjs(createdAt).locale(locale);

  if (!date.isValid()) {
    return '';
  }

  if (dayjs().diff(date, 'day') < 1) {
    return date.fromNow();
  }

  return date.format('D MMM');
}

export function normalizeNotification(notification = {}) {
  const data = getNotificationData(notification);
  const type = getNotificationType(notification);
  const createdAt = getNotificationCreatedAt(notification);
  const orderId = getNotificationOrderId(notification);
  const id = notification._id || notification.id;

  return {
    id,
    title: getNotificationText(notification, 'title'),
    body: getNotificationText(notification, 'description'),
    createdAt,
    dateGroup: getNotificationDateGroup(createdAt),
    iconVariant: notification.iconVariant || NOTIFICATION_ICON_VARIANTS[type] || 'notification',
    isRead: Boolean(notification.is_read ?? notification.isRead),
    loginMeta: type === 'new_login' ? normalizeNotificationLoginMeta(data) : null,
    orderId,
    orderRouteId: orderId,
    time: formatNotificationTime(createdAt),
    type,
  };
}

function normalizeNotificationsResponse(response) {
  return getResponseNotifications(response)
    .map(normalizeNotification)
    .filter((notification) => notification.id);
}

function normalizeNotificationsPageResponse(response, { lastId } = {}) {
  const notifications = normalizeNotificationsResponse(response);
  const nextLastId = notifications.length
    ? notifications[notifications.length - 1].id
    : lastId || null;

  return {
    notifications,
    isMore: Boolean(response?.is_more || response?.isMore),
    lastId: nextLastId,
  };
}

export function normalizeNotificationsCount(value, fallbackCount = 0) {
  const count = Number(value);

  return Number.isFinite(count)
    ? Math.max(0, count)
    : Math.max(0, Number(fallbackCount) || 0);
}

export function getUserNotificationsCount(currentUser, fallbackCount = 0) {
  if (currentUser?.stats?.notifications_count !== undefined && currentUser?.stats?.notifications_count !== null) {
    return normalizeNotificationsCount(currentUser.stats.notifications_count, fallbackCount);
  }

  if (currentUser?.notifications?.unseen !== undefined && currentUser?.notifications?.unseen !== null) {
    return normalizeNotificationsCount(currentUser.notifications.unseen, fallbackCount);
  }

  return normalizeNotificationsCount(fallbackCount);
}

export function updateUserNotificationsCount(currentUser, nextCountOrUpdater, fallbackCount = 0) {
  if (!currentUser) {
    return currentUser;
  }

  const currentCount = getUserNotificationsCount(currentUser, fallbackCount);
  const nextCount = typeof nextCountOrUpdater === 'function'
    ? nextCountOrUpdater(currentCount)
    : nextCountOrUpdater;
  const normalizedCount = normalizeNotificationsCount(nextCount);

  return {
    ...currentUser,
    stats: {
      ...(currentUser.stats || {}),
      notifications_count: normalizedCount,
    },
    ...(currentUser.notifications
      ? {
        notifications: {
          ...currentUser.notifications,
          unseen: normalizedCount,
        },
      }
      : {}),
  };
}

function isNotificationRead(notification) {
  if (notification?.isRead !== undefined && notification?.isRead !== null) {
    return Boolean(notification.isRead);
  }

  if (notification?.is_read !== undefined && notification?.is_read !== null) {
    return Boolean(notification.is_read);
  }

  if (notification?.unread !== undefined && notification?.unread !== null) {
    return !Boolean(notification.unread);
  }

  return false;
}

function markRead(notification) {
  return {
    ...notification,
    isRead: true,
    is_read: true,
    unread: false,
  };
}

export function getNotificationReadRange(notifications, notificationIds) {
  const targetIds = new Set((notificationIds || []).filter(Boolean));

  if (!targetIds.size) {
    return null;
  }

  const targetIndexes = (notifications || []).reduce((indexes, notification, index) => {
    if (targetIds.has(notification.id) && !isNotificationRead(notification)) {
      indexes.push(index);
    }

    return indexes;
  }, []);

  if (!targetIndexes.length) {
    return null;
  }

  const startIndex = Math.min(...targetIndexes);
  const endIndex = Math.max(...targetIndexes);
  const rangeNotifications = (notifications || [])
    .slice(startIndex, endIndex + 1)
    .filter((notification) => notification.id && !isNotificationRead(notification));

  if (!rangeNotifications.length) {
    return null;
  }

  return {
    startId: notifications[startIndex].id,
    endId: notifications[endIndex].id,
    notificationIds: rangeNotifications.map((notification) => notification.id),
  };
}

export function markNotificationRangeAsRead(notifications, { startId, endId } = {}) {
  const currentNotifications = notifications || [];
  const startIndex = currentNotifications.findIndex((notification) => notification.id === startId);
  const endIndex = currentNotifications.findIndex((notification) => notification.id === endId);

  if (startIndex === -1 || endIndex === -1) {
    return {
      notifications: currentNotifications,
      readCount: 0,
    };
  }

  const rangeStartIndex = Math.min(startIndex, endIndex);
  const rangeEndIndex = Math.max(startIndex, endIndex);
  let readCount = 0;

  return {
    notifications: currentNotifications.map((notification, index) => {
      if (index < rangeStartIndex || index > rangeEndIndex || isNotificationRead(notification)) {
        return notification;
      }

      readCount += 1;

      return markRead(notification);
    }),
    readCount,
  };
}

export function markAllNotificationsAsRead(notifications) {
  return (notifications || []).map(markRead);
}

export function groupNotifications(notifications) {
  const groups = new Map();

  (notifications || []).forEach((notification) => {
    const key = notification.dateGroup || 'earlier';
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(notification);
  });

  return NOTIFICATION_GROUP_ORDER
    .filter((key) => groups.has(key))
    .map((key) => ({ key, items: groups.get(key) }));
}

export const notificationsApi = {
  getNotifications({ limit = NOTIFICATIONS_LIMIT, lastId } = {}) {
    return authApi.getNotifications({ limit, lastId }).then(normalizeNotificationsResponse);
  },
  getNotificationsPage({ limit = NOTIFICATIONS_LIMIT, lastId } = {}) {
    return authApi
      .getNotifications({ limit, lastId })
      .then((response) => normalizeNotificationsPageResponse(response, { lastId }));
  },
  markRangeAsSeen({ startId, endId }) {
    return authApi.updateNotifications({
      mode: NOTIFICATION_UPDATE_MODES.MARK_AS_READ,
      all: false,
      startId,
      endId,
    });
  },
  markAsSeen(notificationId) {
    return this.markRangeAsSeen({
      startId: notificationId,
      endId: notificationId,
    });
  },
  markAllAsSeen() {
    return authApi.updateNotifications({
      mode: NOTIFICATION_UPDATE_MODES.MARK_AS_READ,
      all: true,
    });
  },
};

function withReadState(notifications) {
  const readIds = loadReadIds();

  return notifications.map((notification) => ({
    ...notification,
    unread: notification.unread && !readIds.has(notification.id),
  }));
}

export function getNotifications() {
  return withReadState(NOTIFICATIONS);
}

export function getUnreadCount() {
  return getNotifications().filter((notification) => notification.unread).length;
}

export function markAllNotificationsRead() {
  const readIds = new Set(NOTIFICATIONS.map((notification) => notification.id));
  saveReadIds(readIds);
}

export function markNotificationRead(id) {
  const readIds = loadReadIds();
  readIds.add(id);
  saveReadIds(readIds);
}

export {
  NOTIFICATION_READ_BATCH_DELAY_MS,
  NOTIFICATION_READ_BATCH_MAX_WAIT_MS,
  NOTIFICATION_READ_RETRY_DELAY_MS,
};
